import React from 'react'
import PropTypes from 'prop-types'
import {
	BarChart as RechartsBarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	Cell
} from 'recharts'
import './BarChart.scss'

/**
 * BarChart - Responsive bar chart with multiple bars support
 *
 * @param {Array} data - Data array for the chart
 * @param {Array} bars - Bar configurations [{dataKey, color, name, stackId}]
 * @param {string} xAxisKey - Key for x-axis data
 * @param {number} height - Chart height in pixels
 * @param {boolean} showGrid - Show grid lines
 * @param {boolean} showLegend - Show legend
 * @param {boolean} showTooltip - Show tooltip on hover
 * @param {string} title - Chart title
 * @param {string} layout - Chart layout ('vertical' | 'horizontal')
 * @param {boolean} stacked - Stack bars
 * @param {boolean} loading - Show loading state
 */
const BarChart = ({
	data = [],
	bars = [],
	xAxisKey = 'name',
	height = 300,
	showGrid = true,
	showLegend = true,
	showTooltip = true,
	title,
	layout = 'horizontal',
	stacked = false,
	loading = false
}) => {
	const defaultColors = [
		'var(--color-primary)',
		'var(--color-success)',
		'var(--color-warning)',
		'var(--color-danger)',
		'var(--color-info, #6366f1)'
	]

	// Color palette for single bar with multiple data points
	const dataColors = [
		'#3b82f6', // blue
		'#10b981', // green
		'#f59e0b', // amber
		'#ef4444', // red
		'#6366f1', // indigo
		'#8b5cf6', // purple
		'#ec4899', // pink
		'#14b8a6'  // teal
	]

	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			return (
				<div className="bar-chart__tooltip">
					<p className="bar-chart__tooltip-label">{label}</p>
					{payload.map((entry, index) => (
						<p
							key={index}
							className="bar-chart__tooltip-value"
							style={{ color: entry.color }}
						>
							{entry.name}: {entry.value}
						</p>
					))}
				</div>
			)
		}
		return null
	}

	if (loading) {
		return (
			<div className="bar-chart">
				{title && <h3 className="bar-chart__title">{title}</h3>}
				<div className="bar-chart__skeleton" style={{ height }}>
					<div className="skeleton skeleton--chart"></div>
				</div>
			</div>
		)
	}

	if (data.length === 0) {
		return (
			<div className="bar-chart">
				{title && <h3 className="bar-chart__title">{title}</h3>}
				<div className="bar-chart__empty" style={{ height }}>
					<p>No data available</p>
				</div>
			</div>
		)
	}

	const isVertical = layout === 'vertical'

	return (
		<div className="bar-chart">
			{title && <h3 className="bar-chart__title">{title}</h3>}
			<div className="bar-chart__container">
				<ResponsiveContainer width="100%" height={height}>
					<RechartsBarChart
						data={data}
						layout={isVertical ? 'vertical' : 'horizontal'}
						margin={{ top: 5, right: 20, left: isVertical ? 80 : 0, bottom: 5 }}
					>
						{showGrid && (
							<CartesianGrid
								strokeDasharray="3 3"
								stroke="var(--color-gray-200)"
								horizontal={!isVertical}
								vertical={isVertical}
							/>
						)}
						{isVertical ? (
							<>
								<XAxis
									type="number"
									tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
									tickLine={false}
									axisLine={{ stroke: 'var(--color-gray-200)' }}
								/>
								<YAxis
									type="category"
									dataKey={xAxisKey}
									tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
									tickLine={false}
									axisLine={false}
									width={80}
								/>
							</>
						) : (
							<>
								<XAxis
									dataKey={xAxisKey}
									tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
									tickLine={false}
									axisLine={{ stroke: 'var(--color-gray-200)' }}
								/>
								<YAxis
									tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
									tickLine={false}
									axisLine={false}
									width={40}
								/>
							</>
						)}
						{showTooltip && <Tooltip content={<CustomTooltip />} />}
						{showLegend && bars.length > 1 && (
							<Legend
								wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
								iconType="rect"
								iconSize={10}
							/>
						)}
						{bars.map((bar, index) => (
							<Bar
								key={bar.dataKey}
								dataKey={bar.dataKey}
								name={bar.name || bar.dataKey}
								fill={bar.color || defaultColors[index % defaultColors.length]}
								stackId={stacked ? 'stack' : bar.stackId}
								radius={[4, 4, 0, 0]}
							>
								{bar.useDataColors && data.map((entry, i) => (
									<Cell key={`cell-${i}`} fill={dataColors[i % dataColors.length]} />
								))}
							</Bar>
						))}
					</RechartsBarChart>
				</ResponsiveContainer>
			</div>
		</div>
	)
}

BarChart.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object),
	bars: PropTypes.arrayOf(
		PropTypes.shape({
			dataKey: PropTypes.string.isRequired,
			color: PropTypes.string,
			name: PropTypes.string,
			stackId: PropTypes.string,
			useDataColors: PropTypes.bool
		})
	),
	xAxisKey: PropTypes.string,
	height: PropTypes.number,
	showGrid: PropTypes.bool,
	showLegend: PropTypes.bool,
	showTooltip: PropTypes.bool,
	title: PropTypes.string,
	layout: PropTypes.oneOf(['vertical', 'horizontal']),
	stacked: PropTypes.bool,
	loading: PropTypes.bool
}

export default BarChart
